import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, LifeBuoy, Send, MessageSquare, Mail } from 'lucide-react';
import AppShell from '@/components/AppShell';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const DISCORD_URL = 'https://discord.gg/ruJnFDVBn';

const inputCls = 'bg-[#FFFDF9] border-[#E6DED1] focus-visible:ring-[#9D174D]/30';

export default function HelpSupportPage() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) { toast.error('Please add your email and a message.'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/support/contact`, {
        name: name.trim() || email.trim(),
        email: email.trim(),
        message: subject.trim() ? `[${subject.trim()}] ${message}` : message,
      });
      setSent(true);
      setMessage('');
      setSubject('');
      toast.success('Message sent — we\u2019ll be in touch');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send. Please try again.');
    } finally { setSending(false); }
  };

  return (
    <AppShell title="Help & support">
      <SEO title="Help & Support — Poettik" description="Contact Poettik support or join our Discord." noindex />
      <div className="px-4 sm:px-8 py-6 max-w-3xl space-y-6" data-testid="help-support-page">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-[12px] bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center shrink-0">
            <LifeBuoy className="w-5 h-5" strokeWidth={1.8} />
          </span>
          <div>
            <h2 className="font-display text-xl text-[#1F1B16] leading-tight">We're here to help</h2>
            <p className="text-sm text-[#6F675D] mt-0.5">Send us a message and we'll reply by email, or hop into our Discord community.</p>
          </div>
        </div>

        {/* Discord */}
        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 rounded-[16px] border border-[#D9D5F0] bg-[#F2F1FC] p-4 hover:border-[#5865F2] transition-colors"
          data-testid="discord-link"
        >
          <span className="w-10 h-10 rounded-[12px] bg-[#5865F2] text-white flex items-center justify-center shrink-0">
            <MessageSquare className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-[#1F1B16]">Join our Discord community</p>
            <p className="text-xs text-[#6F675D]">Chat with other creators and get quick help from the team.</p>
          </div>
          <span className="shrink-0 text-xs font-semibold text-[#5865F2]">Open →</span>
        </a>

        {/* Contact form */}
        <form onSubmit={submit} className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-5 space-y-4" data-testid="support-form">
          <div className="flex items-center gap-2 text-sm text-[#6F675D]">
            <Mail className="w-4 h-4 text-[#9D174D]" /> Email our support team
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm text-[#1F1B16]">Your name</Label>
              <Input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="support-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-[#1F1B16]">Email *</Label>
              <Input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="support-email" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-[#1F1B16]">Subject</Label>
            <Input className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's it about?" data-testid="support-subject" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-[#1F1B16]">Message *</Label>
            <Textarea className={`${inputCls} min-h-[130px]`} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us how we can help…" data-testid="support-message" />
          </div>
          <Button type="submit" disabled={sending} className="w-full h-11" data-testid="support-submit">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send message</>}
          </Button>
          {sent && <p className="text-sm text-[#15803D] text-center" data-testid="support-sent">Thanks — your message is on its way. We'll reply to your email.</p>}
        </form>
      </div>
    </AppShell>
  );
}
